// AIEnrichmentPanel.jsx - Componente para mostrar datos de enriquecimiento AI
import React, { useState } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  CpuChipIcon, 
  ArrowTrendingUpIcon, 
  BuildingOfficeIcon, 
  UserIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const AIEnrichmentPanel = ({ contact }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!contact?.ai_research_enhanced) {
    return (
      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <CpuChipIcon className="h-6 w-6 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-600">AI Research Not Available</h3>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border">
            Not Enhanced
          </span>
        </div>
        <p className="text-sm text-gray-500">
          This contact hasn't been processed by our AI Research Agent yet.
        </p>
      </div>
    );
  }

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Helper para renderizar chips de classificación
  const renderClassificationChip = (classification) => {
    const colors = {
      'HOT': { bgColor: 'bg-red-100', textColor: 'text-red-800', icon: '🔥' },
      'WARM': { bgColor: 'bg-orange-100', textColor: 'text-orange-800', icon: '⚡' },
      'COLD': { bgColor: 'bg-blue-100', textColor: 'text-blue-800', icon: '❄️' },
      'QUALIFIED': { bgColor: 'bg-green-100', textColor: 'text-green-800', icon: '✅' },
      'UNQUALIFIED': { bgColor: 'bg-gray-100', textColor: 'text-gray-800', icon: '❌' }
    };
    
    const config = colors[classification] || { bgColor: 'bg-gray-100', textColor: 'text-gray-800', icon: '📊' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${config.bgColor} ${config.textColor}`}>
        {config.icon} {classification}
      </span>
    );
  };

  // Helper para renderizar prioridade
  const renderPriorityChip = (priority) => {
    const colors = {
      'ALTA': 'bg-red-100 text-red-800 border-red-300',
      'MEDIA': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'BAIXA': 'bg-blue-100 text-blue-800 border-blue-300',
      'DESCARTE': 'bg-gray-100 text-gray-800 border-gray-300'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors[priority] || colors['DESCARTE']}`}>
        {priority}
      </span>
    );
  };

  // Render legible de un objeto de análisis: pares clave→valor, listas y fuentes enlazadas.
  const humanizeKey = (k) =>
    k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="opacity-60">—</span>;
      return (
        <ul className="list-disc list-inside space-y-0.5">
          {value.map((item, i) => (
            <li key={i}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
          ))}
        </ul>
      );
    }
    if (value && typeof value === 'object') {
      return <span className="opacity-90">{Object.values(value).filter(Boolean).join(' · ')}</span>;
    }
    return <span>{value === null || value === '' ? '—' : String(value)}</span>;
  };

  const renderAnalysis = (obj) => {
    const entries = Object.entries(obj || {}).filter(([k]) => k !== 'sources');
    const sources = (obj && obj.sources) || [];
    return (
      <div className="space-y-2 text-sm">
        {entries.map(([k, v]) => (
          <div key={k}>
            <div className="text-xs font-semibold uppercase opacity-70">{humanizeKey(k)}</div>
            <div className="opacity-95">{renderValue(v)}</div>
          </div>
        ))}
        {sources.length > 0 && (
          <div className="pt-2 mt-2 border-t border-white border-opacity-20">
            <div className="text-xs font-semibold uppercase opacity-70 mb-1">Fontes</div>
            <ul className="space-y-0.5">
              {sources.map((s, i) => (
                <li key={i}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline text-yellow-200 hover:text-yellow-100">
                    {s.title || s.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-blue-700 rounded-lg p-6 mb-6 text-white shadow-lg relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform rotate-45"></div>
      </div>
      
      {/* Header con indicador AI */}
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 rounded-full p-2">
              <CpuChipIcon className="h-8 w-8 text-yellow-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">🤖 AI Research Enhanced</h2>
              <p className="text-sm opacity-90">Powered by AI Research Agent v2.0</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-300" />
            <span className="text-sm font-bold">ENHANCED</span>
          </div>
        </div>

        {/* Scores principales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* AI Lead Score */}
          <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <ArrowTrendingUpIcon className="h-5 w-5" />
              <span className="text-sm font-bold">AI Lead Score</span>
            </div>
            <div className="text-3xl font-bold text-yellow-300 mb-1">
              {contact.ai_final_lead_score || 0}
            </div>
            <div className="text-xs opacity-80">
              vs {contact.lead_score} (original)
            </div>
          </div>

          {/* Classification */}
          <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <ChartBarIcon className="h-5 w-5" />
              <span className="text-sm font-bold">Classification</span>
            </div>
            <div className="mb-2">
              {renderClassificationChip(contact.ai_lead_classification)}
            </div>
            <div className="text-xs opacity-80 flex items-center gap-1">
              Priority: {renderPriorityChip(contact.ai_priority_level)}
            </div>
          </div>

          {/* Deal Potential */}
          <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <StarIcon className="h-5 w-5" />
              <span className="text-sm font-bold">Deal Potential</span>
            </div>
            <div className="text-lg font-bold text-green-300 mb-1">
              {contact.ai_estimated_deal_value || 'N/A'}
            </div>
            <div className="text-xs opacity-80">
              Close: {contact.ai_closing_probability || 0}%
            </div>
          </div>
        </div>

        {/* Professional Information */}
        {contact.ai_verified_position && (
          <div className="bg-white bg-opacity-15 rounded-lg p-4 mb-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <UserIcon className="h-5 w-5" />
              <span className="text-sm font-bold">Verified Position</span>
            </div>
            <div className="text-lg font-bold mb-1">
              {contact.ai_verified_position}
            </div>
            <div className="text-sm opacity-80">
              Decision Power: {contact.ai_decision_power_score || 0}/100 | 
              Quality: {contact.ai_contact_quality_score || 0}/100
            </div>
          </div>
        )}

        {/* Botón para expandir análisis detallado */}
        <div className="flex items-center justify-center">
          <button 
            onClick={handleExpandClick}
            className="flex items-center gap-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-4 py-2 transition-all duration-200"
          >
            {expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
            <span className="text-sm font-medium">
              {expanded ? 'Hide' : 'Show'} Detailed AI Analysis
            </span>
          </button>
        </div>

        {/* Análisis detallado expandible */}
        {expanded && (
          <div className="mt-6 pt-6 border-t border-white border-opacity-30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Analysis */}
              {contact.ai_company_analysis && Object.keys(contact.ai_company_analysis).length > 0 && (
                <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <BuildingOfficeIcon className="h-5 w-5" />
                    <span className="text-sm font-bold">Company Analysis</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {renderAnalysis(contact.ai_company_analysis)}
                  </div>
                </div>
              )}

              {/* Person Analysis */}
              {contact.ai_person_analysis && Object.keys(contact.ai_person_analysis).length > 0 && (
                <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <UserIcon className="h-5 w-5" />
                    <span className="text-sm font-bold">Person Analysis</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {renderAnalysis(contact.ai_person_analysis)}
                  </div>
                </div>
              )}

              {/* Sales Strategy */}
              {contact.ai_sales_strategy && Object.keys(contact.ai_sales_strategy).length > 0 && (
                <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <ArrowTrendingUpIcon className="h-5 w-5" />
                    <span className="text-sm font-bold">Sales Strategy</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {renderAnalysis(contact.ai_sales_strategy)}
                  </div>
                </div>
              )}

              {/* Risk Assessment */}
              {contact.ai_risk_assessment && Object.keys(contact.ai_risk_assessment).length > 0 && (
                <div className="bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span className="text-sm font-bold">Risk Assessment</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {renderAnalysis(contact.ai_risk_assessment)}
                  </div>
                </div>
              )}

              {/* Combined Insights */}
              {contact.ai_combined_insights && Object.keys(contact.ai_combined_insights).length > 0 && (
                <div className="md:col-span-2 bg-white bg-opacity-15 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <CpuChipIcon className="h-5 w-5" />
                    <span className="text-sm font-bold">Combined AI Insights</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {renderAnalysis(contact.ai_combined_insights)}
                  </div>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="mt-4 p-3 bg-black bg-opacity-20 rounded-lg">
              <div className="text-xs opacity-80">
                AI Research Session: {contact.ai_research_session_id} | 
                Sales Cycle: {contact.ai_sales_cycle_estimate || 'Unknown'} |
                Last AI Update: {contact.updated_at ? new Date(contact.updated_at).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIEnrichmentPanel;